'use client';

import { useState, useEffect, useCallback } from 'react';
import { getUsers } from '../api';
import type { UserFilters, UsersListResponse, User } from '../types';

interface UseUsersReturn {
  users: User[];
  total: number;
  page: number;
  limit: number;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
  setPage: (page: number) => void;
  setLimit: (limit: number) => void;
  setFilters: (updater: (prev: UserFilters) => UserFilters) => void;
}

export function useUsers(initialFilters: UserFilters = {}): UseUsersReturn {
  const [filters, setFilters] = useState<UserFilters>(initialFilters);
  const [data, setData] = useState<UsersListResponse>({
    users: [],
    total: 0,
    page: 1,
    limit: 10,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchUsers = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const result = await getUsers(filters);
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch users'));
    } finally {
      setIsLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const setPage = useCallback((page: number) => {
    setFilters((prev) => ({ ...prev, page }));
  }, []);

  const setLimit = useCallback((limit: number) => {
    setFilters((prev) => ({ ...prev, limit }));
  }, []);

  return {
    users: data.users,
    total: data.total,
    page: data.page,
    limit: data.limit,
    isLoading,
    error,
    refetch: fetchUsers,
    setPage,
    setLimit,
    setFilters,
  };
}
